import classNames from "classnames";
import { AddressComponents } from "components/AddressComponent/AddressComponent";
import { stateUpdate } from "apis/stateUpdate";
import { CheckoutModal } from "components/CheckoutModal/CheckoutModal";
import { Button } from "components/DesignSystem/Button/Button";
import { DateInput } from "components/DesignSystem/DateInput/DateInput";
import { TextInput } from "components/DesignSystem/TextInput/TextInput";
import { FieldBuilder } from "components/Entity/EntityFormEngine/FieldBuilder";
import { getByValue } from "components/fileTax/getIndex";
import { SmallSparkle } from "components/icons/SmallSparkle";
import dayjs from "dayjs";
import { Field, FieldProps, Form, Formik, FormikValues } from "formik";
import { useCurrentGroupContext } from "hooks/useCurrentGroupContext";
import { useModal } from "hooks/useModal";
import { useQuery } from "hooks/useQuery";
import { useRoleBasedView } from "hooks/useRoleBasedView";
import { useToast } from "hooks/useToast";
import { useMemo, useState } from "react";
import { useDispatch } from "react-redux";
import { useAddProductsToCartAndCheckoutMutation } from "store/apis/billing";
import {
  EntityDataAttributeWithFieldKey,
  useLazyGetTaskByRequestIdQuery,
} from "store/apis/registrations";
import { useGetAllProductsQuery, Product } from "store/apis/products";
import {
  StateRegistrationNameType,
  stateRegistrationsApis,
  useGetStateRegistrationByIdQuery,
  useUpdateStateRegistrationMutation,
} from "store/apis/StateRegistrations";
import {
  useCreateRegisteredAgentMutation,
  useGetRegisteredAgentQuery,
} from "store/apis/registerAgent";
import { useGetGroupAccountOverviewQuery } from "store/apis/group";
import { useGetFolderOrFileQuery } from "store/apis/previewUrl";
import { Preview } from "components/PreviewModal";
import { BillingInvoice } from "types/Models/billing";
import { STATE_REGISTRATION_PAYROLL } from "types/Models/services";
import { BackendError } from "types/utils/error";
import { openLink } from "utils/openLink";
import { StateRegistrationForm } from "./StateRegistrationForm";
import { EntityDataAttribute } from "types/Models/entity";
import { registerAgentSchema } from "formValidations/registerAgentSchema";

const FileFieldReadOnly = ({
  field,
}: {
  field: EntityDataAttributeWithFieldKey;
}) => {
  const { uuid: groupId } = useCurrentGroupContext();
  const { isOpen, open, close } = useModal();
  const fileIds = Array.isArray(field.value) ? (field.value as string[]) : [];
  const fileId = fileIds[0] ?? null;

  const { data: fileData, isFetching } = useGetFolderOrFileQuery(
    { fileId, groupId },
    { skip: !fileId }
  );

  return (
    <div className="t-flex t-flex-col t-gap-1">
      <span className="t-text-body-sm t-text-text-30">{field.label}</span>
      {fileId ? (
        <>
          <Button
            type="button"
            customType="secondary"
            onClick={open}
            isLoading={isFetching}
            className="t-w-full t-truncate"
          >
            {fileData?.name ?? "View file"}
          </Button>
          <Preview
            showModal={isOpen}
            closeModal={close}
            groupId={groupId}
            previewId={fileId}
          />
        </>
      ) : (
        <span className="t-text-body t-text-text-100">—</span>
      )}
    </div>
  );
};

const InlineRAForm = ({
  stateName,
  groupId,
  entityId,
  onSuccess,
  onCancel,
  onNotNeeded,
  isNotNeededLoading,
  hideActions = false,
  formRef,
  onValidityChange,
}: {
  stateName: string;
  groupId: string;
  entityId: string;
  onSuccess: () => void;
  onCancel?: () => void;
  onNotNeeded?: () => void;
  isNotNeededLoading?: boolean;
  hideActions?: boolean;
  formRef?: React.MutableRefObject<{
    submitForm: () => void;
    isValid: boolean;
  } | null>;
  onValidityChange?: (isValid: boolean) => void;
}) => {
  const { alertToast, successToast } = useToast();
  const [createRegisteredAgent] = useCreateRegisteredAgentMutation();
  const [selectedAddressId, setSelectedAddressId] = useState<
    string | undefined
  >(undefined);

  const saveRA = async ({
    start_date,
    expiration_date,
    agent_name,
    street_address,
    street_address_line_2,
    city,
    state,
    zipcode,
    country,
    phone_number,
  }: FormikValues) => {
    try {
      await createRegisteredAgent({
        payload: {
          agent_name,
          street_address,
          street_address_line_2,
          city,
          state,
          zipcode,
          country,
          phone_number,
          expiration_date: dayjs(expiration_date).format("YYYY-MM-DD"),
          start_date: dayjs(start_date).format("YYYY-MM-DD"),
        },
        groupId,
        entityId,
      }).unwrap();
      successToast({ message: "Registered Agent Added" });
      onSuccess();
    } catch (error: any) {
      alertToast({ message: error?.data?.error?.message });
      throw error;
    }
  };

  return (
    <Formik
      onSubmit={saveRA}
      validationSchema={registerAgentSchema}
      innerRef={(f) => {
        if (formRef && f)
          formRef.current = { submitForm: f.submitForm, isValid: f.isValid };
        onValidityChange?.(f?.isValid ?? false);
      }}
      initialValues={{
        agent_name: "",
        start_date: "",
        expiration_date: "",
        street_address: "",
        street_address_line_2: null,
        city: "",
        state: "",
        zipcode: "",
        country: "",
        phone_number: "",
      }}
      validateOnChange
      validateOnBlur={false}
    >
      {({ isSubmitting, setFieldValue, submitForm }) => (
        <div className="t-flex t-flex-col t-gap-4">
          <TextInput
            name="agent_name"
            block
            required
            label="Agent Name"
            placeholder="Enter Agent Name..."
          />
          <div className="t-flex t-gap-4 sm:t-flex-col">
            <Field name="start_date">
              {({ field }: FieldProps) => (
                <div className="t-flex-1">
                  <DateInput
                    {...field}
                    required
                    portalId={field.name}
                    label="Starting date"
                    placeholder="Enter starting date"
                  />
                </div>
              )}
            </Field>
            <Field name="expiration_date">
              {({ field }: FieldProps) => (
                <div className="t-flex-1">
                  <DateInput
                    required
                    {...field}
                    portalId={field.name}
                    label="Expiration date"
                    placeholder="Enter expiration date"
                  />
                </div>
              )}
            </Field>
          </div>
          <AddressComponents
            label="Address"
            required
            groupId={groupId ?? ""}
            entityId={entityId ?? ""}
            selectedAddressId={selectedAddressId}
            onAddressSelect={(address) => {
              setSelectedAddressId(address?.uuid ?? undefined);
              setFieldValue("street_address", address?.street_address ?? "");
              setFieldValue(
                "street_address_line_2",
                address?.street_address_line_2 ?? null
              );
              setFieldValue("city", address?.city ?? "");
              setFieldValue("state", address?.state ?? "");
              setFieldValue("zipcode", address?.zipcode ?? "");
              setFieldValue("country", address?.country ?? "");
            }}
          />
          <TextInput
            name="phone_number"
            required
            label="Phone No."
            placeholder="Enter Phone No..."
          />
          {!hideActions && (
            <div className="t-flex t-items-center t-justify-between t-gap-3">
              <span />
              <div className="t-flex t-items-center t-gap-3">
                {onCancel && (
                  <Button
                    customType="ghost"
                    size="small"
                    type="button"
                    onClick={onCancel}
                  >
                    Cancel
                  </Button>
                )}
                <Button
                  customType="primary"
                  size="small"
                  type="button"
                  onClick={submitForm}
                  isLoading={isSubmitting}
                  disabled={isSubmitting}
                >
                  Save changes
                </Button>
              </div>
            </div>
          )}
        </div>
      )}
    </Formik>
  );
};

const FALLBACK_ACTION_LABEL: Record<StateRegistrationNameType, string> = {
  SECRETARY_OF_STATE: "Let Inkle file your foreign qualification",
  REGISTERED_AGENT: "Assign Inkle as your registered agent",
  PAY_ROLL: "Let Inkle set up your payroll registration",
};

type Props = {
  stateId: string;
  stateType: StateRegistrationNameType;
  stateName: string;
  flat?: boolean;
  onLetInkleHandle?: () => void;
  cartStatus?: string | null;
  startInEditMode?: boolean;
  hideAlreadyRegisteredLabel?: boolean;
  hideInnerBorder?: boolean;
  hideBottomActions?: boolean;
  onNotNeeded?: () => void;
  isNotNeededLoading?: boolean;
  formikRef?: React.MutableRefObject<{
    submitForm: () => void;
    isValid: boolean;
  } | null>;
  onSaveSuccess?: () => void;
  onValidityChange?: (isValid: boolean) => void;
};

export const StateRegistrationCard = ({
  stateId,
  stateType,
  stateName,
  flat = false,
  onLetInkleHandle,
  cartStatus: cartStatusProp,
  startInEditMode = false,
  hideAlreadyRegisteredLabel = false,
  hideInnerBorder = false,
  hideBottomActions = false,
  onNotNeeded,
  isNotNeededLoading = false,
  formikRef,
  onSaveSuccess,
  onValidityChange,
}: Props) => {
  const { alertToast, successToast } = useToast();
  const { uuid: groupId } = useCurrentGroupContext();
  const query = useQuery();
  const entityId = query.get("entity");
  const dispatch = useDispatch();
  const { isForeignCA } = useRoleBasedView();

  const { data: allProducts } = useGetAllProductsQuery(
    { groupId, entityId: entityId! },
    { skip: !groupId || !entityId }
  );

  const payrollProduct = Object.values(allProducts || {})
    .flatMap((group) =>
      Object.values(group).flatMap((items) =>
        Array.isArray(items) ? (items as Product[]) : []
      )
    )
    .find((p) => p.base_task_key === STATE_REGISTRATION_PAYROLL);

  const { data: registration, isLoading } = useGetStateRegistrationByIdQuery(
    { groupId, entityId: entityId!, stateId, stateType },
    { skip: !groupId || !entityId || !stateId || !stateType }
  );

  const [updateRegistration, { isLoading: isSaving }] =
    useUpdateStateRegistrationMutation();

  const registrationFormModal = useModal();
  const filingPaymentModal = useModal();

  const [getTaskByRequestId] = useLazyGetTaskByRequestIdQuery();
  const [createProductInvoice, { data: productInvoice }] =
    useAddProductsToCartAndCheckoutMutation();

  const { data: accountOverview } = useGetGroupAccountOverviewQuery(
    { entityId: entityId!, season: null },
    { skip: !entityId || stateType !== "SECRETARY_OF_STATE" }
  );

  const coiFileId =
    accountOverview?.company_profile?.formation_documents
      ?.certificate_of_incorporation?.file_id ?? null;

  const { data: registeredAgents = [] } = useGetRegisteredAgentQuery(
    { groupId: groupId!, entityId: entityId! },
    {
      skip: !groupId || !entityId || stateType !== "REGISTERED_AGENT",
    }
  );

  const activeRA =
    registeredAgents.find(
      (ra) => ra.state === stateName && ra.status === "ACTIVE"
    ) ??
    (onNotNeeded
      ? registeredAgents
          .filter((ra) => ra.status === "ACTIVE")
          .sort((a, b) =>
            (b.start_date ?? "").localeCompare(a.start_date ?? "")
          )[0]
      : undefined);

  const activeRAAddress = activeRA
    ? [
        activeRA.street_address,
        activeRA.street_address_line_2,
        activeRA.city,
        activeRA.state,
        activeRA.zipcode,
      ]
        .filter(Boolean)
        .join(", ")
    : undefined;

  const activeRAFields = [
    { label: "Agent Name", value: activeRA?.agent_name },
    { label: "Starting Date", value: activeRA?.start_date },
    { label: "Expiration Date", value: activeRA?.expiration_date },
    { label: "Address", value: activeRAAddress },
    { label: "Phone No.", value: activeRA?.phone_number },
  ];

  const isNotApplicable = registration?.status === "NOT_APPLICABLE";
  const isNotRegistered =
    !isNotApplicable &&
    (registration?.status === "NOT_REGISTERED" ||
      registration?.status === "REQUIRED" ||
      registration?.status === "FOREIGN_QUALIFICATION_REQUIRED");
  const isInCart = (cartStatusProp ?? registration?.cart_status) === "IN_CART";

  const launchFiling = async () => {
    if (!groupId || !registration?.filing_data) return;

    try {
      const price = registration.filing_data.amount;
      const isPayroll = stateType === "PAY_ROLL";
      const payload = {
        entity_id: entityId!,
        product_content_type_id: isPayroll
          ? payrollProduct?.product_content_type_id!
          : registration.filing_data.product_content_type_id,
        product_id: isPayroll
          ? payrollProduct?.uuid!
          : registration.filing_data.base_task_template_id,
        state_id: isPayroll ? stateId : undefined,
        quantity: 1,
        payment_amount: !price || isNaN(Number(price)) ? 0 : Number(price),
      };

      await createProductInvoice({
        groupId,
        entityId: entityId!,
        productData: [payload],
        from_other: true,
      }).unwrap();

      filingPaymentModal.open();
    } catch (error) {
      alertToast({ message: (error as BackendError).data?.error?.message });
    }
  };

  const closeFilingPaymentModal = () => {
    filingPaymentModal.close();
  };

  const getNewTaskDetail = async () => {
    try {
      dispatch(
        stateRegistrationsApis.util.invalidateTags(["STATE_REGISTRATIONS"])
      );
      const taskData = await getTaskByRequestId({
        groupId,
        requestId: productInvoice?.request_id!,
        entityId: entityId!,
      }).unwrap();

      if (taskData && taskData.length > 0) {
        const task = taskData[0];
        const current = task?.task_states?.[0].uuid;
        const check = new Map();
        const indexing = new Map();

        task?.task_states?.forEach((element, index) => {
          check.set(element.uuid, element.type);
          indexing.set(element.uuid, index);
        });

        const nextStateUuid = getByValue(indexing, indexing.get(current) + 1);
        await stateUpdate({ taskId: task.uuid, id: nextStateUuid });
        successToast({ message: "Filing launched successfully!" });

        if (task?.uuid) {
          openLink(`/tax/filings/${task.uuid}`);
        }
      }
    } catch (error) {
      alertToast({ message: (error as BackendError)?.data?.error?.message });
    }
    closeFilingPaymentModal();
  };

  const openShareDetails = () => {
    if (registration?.action_type === "LAUNCH_FILING") {
      launchFiling();
    } else {
      registrationFormModal.open();
    }
  };

  const onRASuccess = async () => {
    try {
      if (groupId && entityId && registration) {
        await updateRegistration({
          groupId,
          entityId,
          stateId,
          payload: {
            data_attributes: registration.data_attributes,
            type: stateType,
            status: "REGISTERED",
          },
        }).unwrap();
        setIsEditing(false);
      }
    } catch (error) {
      alertToast({ message: (error as BackendError)?.data?.error?.message });
    }
  };

  const initialValues = useMemo(
    () =>
      registration?.data_attributes.reduce((ac, cv) => {
        const isEmpty =
          cv.value === null ||
          cv.value === undefined ||
          (Array.isArray(cv.value) && cv.value.length === 0);
        const isCOIField = cv.name === "certificate_of_incorporation";
        const prefilled =
          isEmpty && isCOIField && coiFileId ? [coiFileId] : cv.value;
        const key = cv.field_key || cv.name;
        return { ...ac, [key]: prefilled, [cv.name]: prefilled };
      }, {}) ?? {},
    [registration?.data_attributes, coiFileId]
  );

  const handleSave = async (values: Record<string, unknown>) => {
    if (!groupId || !entityId) return;
    try {
      const updatedAttributes = registration!.data_attributes.map((attr) => ({
        ...attr,
        value: (values[attr.field_key || attr.name] ??
          attr.value) as EntityDataAttribute["value"],
      }));
      await updateRegistration({
        groupId,
        entityId,
        stateId,
        payload: { data_attributes: updatedAttributes, type: stateType },
      }).unwrap();
      successToast({ message: "Details saved successfully!" });
      setIsEditing(false);
      dispatch(
        stateRegistrationsApis.util.invalidateTags(["STATE_REGISTRATIONS"])
      );
      onSaveSuccess?.();
    } catch (error) {
      alertToast({ message: (error as BackendError)?.data?.error?.message });
      throw error;
    }
  };

  const [isEditing, setIsEditing] = useState(startInEditMode);

  const handleMarkNotNeeded = async () => {
    if (!groupId || !entityId) return;
    try {
      await updateRegistration({
        groupId,
        entityId,
        stateId,
        payload: {
          data_attributes: registration!.data_attributes,
          type: stateType,
          status: "NOT_APPLICABLE",
        },
      }).unwrap();
      successToast({ message: "Marked as not needed." });
      dispatch(
        stateRegistrationsApis.util.invalidateTags(["STATE_REGISTRATIONS"])
      );
    } catch (error) {
      alertToast({ message: (error as BackendError)?.data?.error?.message });
    }
  };

  const sortedFields = registration?.data_attributes.toSorted(
    (a, b) => a.order - b.order
  );

  if (isLoading || !registration) {
    return (
      <div className="t-flex t-items-center t-justify-center t-h-40 t-text-text-30 t-text-body-sm">
        Loading...
      </div>
    );
  }

  return (
    <>
      <Formik
        onSubmit={handleSave}
        initialValues={initialValues}
        enableReinitialize
        innerRef={(f) => {
          if (formikRef && f)
            formikRef.current = {
              submitForm: f.submitForm,
              isValid: f.isValid,
            };
          onValidityChange?.(f?.isValid ?? false);
        }}
      >
        {({ submitForm, values }) => {
          const requiredFields =
            sortedFields?.filter((f) => f.is_required) ?? [];
          const allRequiredFilled = requiredFields.every((f) => {
            const val = (values as Record<string, unknown>)[f.field_key];
            return val !== null && val !== undefined && val !== "";
          });

          return (
            <Form className="all:unset t-flex t-flex-col t-h-full">
              <div className="t-flex t-flex-col t-gap-4 t-p-5 t-flex-1 t-overflow-auto sm:t-p-2">
                {/* Fields box — read-only always, edit mode when isEditing */}
                {sortedFields && sortedFields.length > 0 && !isEditing && (
                  <div className="t-flex t-flex-col t-gap-4 t-rounded-lg t-border t-border-solid t-border-neutral-10 t-p-4 sm:t-border-0 sm:t-p-0">
                    {sortedFields.map((field) => {
                      const isFileField =
                        field.type === "FILE" ||
                        field.type === "MULTI_FILE_INPUT";
                      if (isFileField) {
                        return (
                          <FileFieldReadOnly
                            key={field.field_key || field.name}
                            field={field}
                          />
                        );
                      }
                      return (
                        <div
                          key={field.field_key}
                          className="t-flex t-flex-col t-gap-1"
                        >
                          <span className="t-text-body-sm t-text-text-30">
                            {field.label}
                          </span>
                          <span className="t-text-body t-text-text-100">
                            {field.value &&
                            !(
                              Array.isArray(field.value) &&
                              field.value.length === 0
                            )
                              ? String(field.value)
                              : "—"}
                          </span>
                        </div>
                      );
                    })}
                  </div>
                )}

                {/* Fields box — edit mode (only for already-registered states) */}
                {sortedFields &&
                  sortedFields.length > 0 &&
                  isEditing &&
                  !isNotRegistered && (
                    <div className="t-flex t-flex-col t-gap-4 t-rounded-lg t-border t-border-solid t-border-neutral-10 t-p-4 sm:t-border-0 sm:t-p-0">
                      {sortedFields.map((field, i) => (
                        <FieldBuilder
                          key={i}
                          field={
                            field.type === "STRING" || field.type === "NUMBER"
                              ? { ...field, type: "INPUT" }
                              : field
                          }
                        />
                      ))}
                    </div>
                  )}

                {/* RA — read-only details */}
                {stateType === "REGISTERED_AGENT" && !isEditing && (
                  <div className="t-flex t-flex-col t-gap-4 t-rounded-lg t-border t-border-solid t-border-neutral-10 t-p-4 sm:t-border-0 sm:t-p-0">
                    {activeRAFields.map(({ label, value }) => (
                      <div key={label} className="t-flex t-flex-col t-gap-1">
                        <span className="t-text-body-sm t-text-text-30">
                          {label}
                        </span>
                        <span className="t-text-body t-text-text-100">
                          {value || "—"}
                        </span>
                      </div>
                    ))}
                  </div>
                )}

                {/* Not registered — form (shown in edit mode) */}
                {isNotRegistered &&
                  isEditing &&
                  (stateType === "REGISTERED_AGENT" ? (
                    <div
                      className={classNames("t-flex t-flex-col t-gap-4", {
                        "t-rounded-lg t-border t-border-solid t-border-neutral-10 t-p-4":
                          !hideInnerBorder,
                      })}
                    >
                      {!hideAlreadyRegisteredLabel && (
                        <span className="t-text-body-sm t-font-semibold t-text-text-60">
                          Already have a registered agent?
                        </span>
                      )}
                      <InlineRAForm
                        stateName={stateName}
                        groupId={groupId ?? ""}
                        entityId={entityId ?? ""}
                        onSuccess={onRASuccess}
                        onCancel={
                          hideBottomActions
                            ? undefined
                            : () => setIsEditing(false)
                        }
                        onNotNeeded={onNotNeeded}
                        isNotNeededLoading={isNotNeededLoading}
                        hideActions={hideBottomActions}
                        formRef={hideBottomActions ? formikRef : undefined}
                        onValidityChange={
                          hideBottomActions ? onValidityChange : undefined
                        }
                      />
                    </div>
                  ) : (
                    <div
                      className={classNames("t-flex t-flex-col t-gap-4", {
                        "t-rounded-lg t-border t-border-solid t-border-neutral-10 t-p-4":
                          !hideInnerBorder,
                      })}
                    >
                      {!hideAlreadyRegisteredLabel && (
                        <span className="t-text-body-sm t-font-semibold t-text-text-60">
                          Already registered? Add your account number
                        </span>
                      )}
                      {sortedFields && sortedFields.length > 0 ? (
                        <div className="t-flex t-flex-col t-gap-4">
                          {sortedFields.map((field, i) => (
                            <FieldBuilder
                              key={i}
                              field={
                                field.type === "STRING" ||
                                field.type === "NUMBER"
                                  ? { ...field, type: "INPUT" }
                                  : field
                              }
                            />
                          ))}
                        </div>
                      ) : (
                        <div className="t-text-body-sm t-text-text-30">
                          Loading fields...
                        </div>
                      )}
                    </div>
                  ))}

                {/* Bottom action row — not shown for RA (buttons are inside InlineRAForm) */}
                {isEditing &&
                stateType !== "REGISTERED_AGENT" &&
                !hideBottomActions ? (
                  <div className="t-flex t-items-center t-justify-between t-gap-3">
                    <span />
                    <div className="t-flex t-items-center t-gap-3">
                      <Button
                        type="button"
                        customType="ghost"
                        size="small"
                        onClick={() => setIsEditing(false)}
                      >
                        Cancel
                      </Button>
                      {(registration.data_attributes.length > 0 ||
                        onNotNeeded) && (
                        <Button
                          type="button"
                          customType="primary"
                          size="small"
                          onClick={submitForm}
                          isLoading={isSaving}
                          disabled={!allRequiredFilled}
                        >
                          Save changes
                        </Button>
                      )}
                    </div>
                  </div>
                ) : (
                  isNotRegistered &&
                  !isInCart &&
                  !hideBottomActions &&
                  !onNotNeeded && (
                    <div className="t-flex t-items-center t-justify-between t-gap-3">
                      <Button
                        type="button"
                        customType="primary"
                        size="small"
                        onClick={onLetInkleHandle ?? openShareDetails}
                      >
                        <SmallSparkle className="t-shrink-0" />
                        {FALLBACK_ACTION_LABEL[stateType]}
                      </Button>
                      <Button
                        type="button"
                        customType="ghost"
                        size="small"
                        onClick={handleMarkNotNeeded}
                        isLoading={isSaving}
                      >
                        Not applicable
                      </Button>
                    </div>
                  )
                )}
              </div>
            </Form>
          );
        }}
      </Formik>

      {registrationFormModal.isOpen && registration && (
        <StateRegistrationForm
          isOpen={registrationFormModal.isOpen}
          close={registrationFormModal.close}
          fields={registration.data_attributes}
          stateId={stateId}
          stateType={stateType}
        />
      )}

      {filingPaymentModal.isOpen &&
        Boolean(productInvoice?.invoice) &&
        entityId && (
          <CheckoutModal
            entityId={entityId}
            type="invoices"
            onInvoicePaid={getNewTaskDetail}
            open={filingPaymentModal.isOpen}
            onClose={closeFilingPaymentModal}
            invoices={[productInvoice?.invoice as BillingInvoice]}
            invoiceRequestId={productInvoice?.request_id}
            payerUserType={isForeignCA && "FCA"}
          />
        )}
    </>
  );
};
